/* ════ CONFIG ══════════════════════════════════════════════ */
// Supabase publishable (anon) credentials. These are public by design;
// Row Level Security + Supabase Auth govern access. Override at build time
// with VITE_SB_URL / VITE_SB_KEY (see .env.example).
export const SB_URL = import.meta.env.VITE_SB_URL || 'https://xsguhiqrzgoplmaxpzys.supabase.co';
export const SB_KEY = import.meta.env.VITE_SB_KEY || 'sb_publishable_s4fFAXoTiNGobEKTpR5dfA_E2Z_XmVK';

// Name of the tentative planner block shown while a leave request awaits approval.
export const LEAVE_PENDING_NAME = 'Annual Leave (pending)';
export const APP_VERSION = 'v1.45';
// Shown once per version in the "what's new" popup (and on the login screen).
export const WHATS_NEW = '🛠️ Small fixes and improvements.';

export const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const RES_COLS = ['#7F77DD', '#1A6FD4', '#059669', '#B5379A', '#D97706', '#DC2626', '#0891B2', '#9CA3AF'];
export const CAT_LBL = { ooo: 'OOO/Leave', output: 'Output', ai: 'AI', ext: 'External', uncat: 'Uncategorised' };
export const CAT_ICON = { ooo: '🔴', output: '🔵', ai: '🟢', ext: '🟣', uncat: '⚪' };
// The five built-in categories (kept in code so they never change look). Custom
// ones live in the `categories` table and render with their own colour.
export const DEFAULT_CATS = [
  { id: 'ooo', name: 'OOO/Leave', icon: '🔴', color: '#ED1F23', sort: 1 },
  { id: 'output', name: 'Output', icon: '🔵', color: '#1A6FD4', sort: 2 },
  { id: 'ai', name: 'AI', icon: '🟢', color: '#059669', sort: 3 },
  { id: 'ext', name: 'External', icon: '🟣', color: '#B5379A', sort: 4 },
  { id: 'uncat', name: 'None', icon: '⚪', color: '#9CA3AF', sort: 5 },
];

// Importance levels (low / normal / high), shown on cards and blocks.
export const IMPORTANCE = [
  { v: 'low', l: 'Low', mark: '↓' },
  { v: 'normal', l: 'Normal', mark: '' },
  { v: 'high', l: 'High', mark: '!' },
];

// Suggested roles for resources (free-text — used to seed the picker datalist;
// any value the team already uses is added on top of these).
export const ROLE_SUGGESTIONS = ['Consultant', 'Designer', 'Project Support', 'AI', 'Developer', 'Account Manager', 'Associate'];

// ── Travel / CO₂ board ──────────────────────────────────────
export const TRAVEL_MODES = [
  { v: 'flight', l: 'Flight', ic: '✈️' },
  { v: 'train', l: 'Train', ic: '🚆' },
  { v: 'car', l: 'Car', ic: '🚗' },
  { v: 'hotel', l: 'Hotel', ic: '🏨' },
  { v: 'other', l: 'Other', ic: '•' },
];
export const TRAVEL_ZONES = ['UK', 'EU', 'US'];
// Rough emission factors (tonnes CO₂e). Flights/car/train are per one-way leg;
// hotel is per night. Deliberately an *estimate* — always overridable.
export const CO2_EST = {
  flight: { UK: 0.10, EU: 0.20, US: 1.00 },
  train:  { UK: 0.006, EU: 0.02, US: 0.03 },
  car:    { UK: 0.03, EU: 0.05, US: 0.06 },
  hotel:  { UK: 0.015, EU: 0.02, US: 0.025 },
  other:  { UK: 0, EU: 0, US: 0 },
};
// ── Flights: UK Gov 2026 GHG conversion factors ─────────────
// Air, "to/from UK", BUSINESS CLASS, incl. radiative forcing (kg CO₂e per
// passenger·km). Domestic has no class split (single-class factor).
export const FLIGHT_FACTOR = { domestic: 0.22928, short: 0.18863, long: 0.33940 };
// Trip zone → UK-relative haul band (we only count flights to/from the UK).
export const ZONE_HAUL = { UK: 'domestic', EU: 'short', US: 'long' };
// Representative one-way distances (km) when the exact distance isn't known.
export const HAUL_KM = { domestic: 400, short: 1500, long: 6000 };
// Tonnes CO₂e for a flight: distance (km, each way) × business factor.
export function flightCo2(zone, km, returnTrip = false) {
  const haul = ZONE_HAUL[zone] || 'short';
  const dist = (Number(km) || HAUL_KM[haul]) * (returnTrip ? 2 : 1);
  return Math.round(dist * FLIGHT_FACTOR[haul]) / 1000;
}

export function estimateCo2(mode, zone, nights = 1, returnTrip = false) {
  if (mode === 'flight') return flightCo2(zone, 0, returnTrip);
  const base = (CO2_EST[mode] || {})[zone] || 0;
  const t = mode === 'hotel' ? base * Math.max(1, nights) : base * (returnTrip ? 2 : 1);
  return Math.round(t * 1000) / 1000;
}

export const DEFAULT_RES = [
  { id: 'unassigned', name: 'Unassigned', ini: 'UN', col: '#9CA3AF', sort: 0 },
  { id: 'karen', name: 'Karen', ini: 'KA', col: '#7F77DD', sort: 1 },
  { id: 'robin', name: 'Robin', ini: 'RO', col: '#1A6FD4', sort: 2 },
  { id: 'fiona', name: 'Fiona', ini: 'FI', col: '#059669', sort: 3 },
  { id: 'vanessa', name: 'Vanessa', ini: 'VA', col: '#B5379A', sort: 4 },
];
